import React, { useState } from 'react';
import { Plus, Pencil, Trash2, X, Server, AtSign } from 'lucide-react';
import api from '../../api';

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400";

const emptyForm = {
    name: '',
    fromEmail: '',
    fromName: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpStarttls: true,
    smtpAuth: true,
    enabled: true,
};

/**
 * Отправители: адреса, от имени которых уходят письма.
 *
 * <p>Пароль SMTP не показывается никогда - его нет и в ответе сервера. Пустое поле при
 * правке означает «оставить прежний»: иначе смена названия требовала бы заново вводить
 * чужой пароль.
 */
const MailSendersSection = ({ senders, onChanged, onAlert }) => {
    const [form, setForm] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...emptyForm });
    };

    const openEdit = (sender) => {
        setEditingId(sender.id);
        setForm({
            name: sender.name,
            fromEmail: sender.fromEmail,
            fromName: sender.fromName || '',
            smtpHost: sender.smtpHost || '',
            smtpPort: sender.smtpPort || 587,
            smtpUsername: sender.smtpUsername || '',
            smtpPassword: '',
            smtpStarttls: sender.smtpStarttls,
            smtpAuth: sender.smtpAuth,
            enabled: sender.enabled,
        });
    };

    const errorText = (err, fallback) =>
        err?.response?.data?.message || (typeof err?.response?.data === 'string' && err.response.data) || fallback;

    const handleSave = () => {
        const payload = {
            ...form,
            smtpPort: form.smtpPort ? Number(form.smtpPort) : null,
            smtpHost: form.smtpHost.trim() || null,
            smtpUsername: form.smtpUsername.trim() || null,
            smtpPassword: form.smtpPassword.trim() || null,
            fromName: form.fromName.trim() || null,
        };
        const request = editingId
            ? api.put(`/api/admin/mail/senders/${editingId}`, payload)
            : api.post('/api/admin/mail/senders', payload);

        request
            .then(() => {
                setForm(null);
                onChanged();
                onAlert('success', editingId ? 'Отправитель сохранён' : 'Отправитель добавлен');
            })
            .catch(err => onAlert('error', errorText(err, 'Не удалось сохранить отправителя')));
    };

    const handleDelete = (sender) => {
        if (!window.confirm(`Удалить отправителя «${sender.name}»? Письма с этого адреса отправлять станет нельзя.`)) return;
        api.delete(`/api/admin/mail/senders/${sender.id}`)
            .then(() => {
                onChanged();
                onAlert('success', 'Отправитель удалён');
            })
            .catch(err => onAlert('error', errorText(err, 'Не удалось удалить отправителя')));
    };

    return (
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
            <div className="flex items-start justify-between mb-1">
                <h3 className="m-0 text-lg font-semibold text-gray-900">Отправители</h3>
                <button className={primaryBtn + ' flex items-center gap-1.5'} onClick={openCreate}>
                    <Plus size={16} /> Добавить
                </button>
            </div>
            <p className="text-gray-500 text-sm mb-5">
                Адрес в поле «От кого» и почтовый сервер, через который уходит письмо. У сервиса
                со своим доменом должен быть и свой SMTP: письмо с чужого домена, отправленное
                нашим сервером, принимающая сторона считает подделкой.
            </p>

            {senders.length === 0 ? (
                <p className="text-sm text-gray-500 m-0">
                    Отправителей пока нет — письма уходят с системного адреса сервиса.
                </p>
            ) : (
                <ul className="list-none p-0 m-0">
                    {senders.map(sender => (
                        <li key={sender.id} className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-b-0">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900">{sender.name}</span>
                                    {!sender.enabled && (
                                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                            выключен
                                        </span>
                                    )}
                                </div>
                                <div className="text-[13px] text-gray-500 flex items-center gap-1.5 mt-1">
                                    <AtSign size={13} className="shrink-0" />
                                    {sender.fromName ? `${sender.fromName} <${sender.fromEmail}>` : sender.fromEmail}
                                </div>
                                <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-1">
                                    <Server size={12} className="shrink-0" />
                                    {sender.ownTransport
                                        ? `${sender.smtpHost}:${sender.smtpPort || 587}${sender.hasPassword ? '' : ' · без пароля'}`
                                        : 'системный транспорт сервиса'}
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button className={secondaryBtn + ' flex items-center gap-1.5'} onClick={() => openEdit(sender)}>
                                    <Pencil size={14} /> Изменить
                                </button>
                                <button
                                    className="bg-transparent text-gray-400 p-2 border border-transparent rounded-md cursor-pointer transition-all hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                                    title="Удалить отправителя"
                                    onClick={() => handleDelete(sender)}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {form && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setForm(null)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-[520px] max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="m-0 text-lg font-semibold text-gray-900">
                                {editingId ? 'Отправитель' : 'Новый отправитель'}
                            </h3>
                            <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-1" onClick={() => setForm(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Название</label>
                            <input className={inputClass} value={form.name} placeholder="Конструктор"
                                   onChange={e => setForm({ ...form, name: e.target.value })} />
                            <span className="text-[13px] text-gray-500">Видно только в админке.</span>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Адрес отправителя</label>
                            <input className={inputClass} type="email" value={form.fromEmail} placeholder="no-reply@service.ru"
                                   onChange={e => setForm({ ...form, fromEmail: e.target.value })} />
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Отображаемое имя</label>
                            <input className={inputClass} value={form.fromName} placeholder="Конструктор IT-Burger"
                                   onChange={e => setForm({ ...form, fromName: e.target.value })} />
                        </div>

                        <div className="h-px bg-gray-200 my-5" />

                        <p className="text-[13px] text-gray-500 mb-4">
                            Оставьте сервер пустым, чтобы отправлять через системный транспорт сервиса
                            авторизации. Для чужого домена так делать не стоит: письмо не пройдёт проверку SPF.
                        </p>

                        <div className="mb-4 flex gap-3">
                            <div className="flex flex-col gap-1.5 flex-1">
                                <label className="text-sm font-medium text-gray-700">SMTP-сервер</label>
                                <input className={inputClass} value={form.smtpHost} placeholder="smtp.service.ru"
                                       onChange={e => setForm({ ...form, smtpHost: e.target.value })} />
                            </div>
                            <div className="flex flex-col gap-1.5 w-[110px]">
                                <label className="text-sm font-medium text-gray-700">Порт</label>
                                <input className={inputClass} type="number" value={form.smtpPort}
                                       onChange={e => setForm({ ...form, smtpPort: e.target.value })} />
                            </div>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Логин</label>
                            <input className={inputClass} value={form.smtpUsername}
                                   onChange={e => setForm({ ...form, smtpUsername: e.target.value })} />
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Пароль</label>
                            <input className={inputClass} type="password" value={form.smtpPassword}
                                   placeholder={editingId ? 'оставьте пустым, чтобы не менять' : ''}
                                   onChange={e => setForm({ ...form, smtpPassword: e.target.value })} />
                            <span className="text-[13px] text-gray-500">
                                Хранится в зашифрованном виде и обратно не показывается.
                            </span>
                        </div>

                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-2">
                            <input type="checkbox" checked={form.smtpAuth}
                                   onChange={e => setForm({ ...form, smtpAuth: e.target.checked })} />
                            Авторизация на сервере
                        </label>
                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-2">
                            <input type="checkbox" checked={form.smtpStarttls}
                                   onChange={e => setForm({ ...form, smtpStarttls: e.target.checked })} />
                            STARTTLS
                        </label>
                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-5">
                            <input type="checkbox" checked={form.enabled}
                                   onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                            Включён
                        </label>

                        <div className="flex gap-3">
                            <button className={primaryBtn} onClick={handleSave}>Сохранить</button>
                            <button className={secondaryBtn} onClick={() => setForm(null)}>Отмена</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailSendersSection;
